<template>
  <h5 class="mb-3">{{ name }}</h5>

  <div class="form-floating mb-4">
    <currency-input
      ref="uiAmount"
      description="annual amount"
      elementId="otherIncomeAmount"
      @entered="onSaveShortcut"
      ariaLabel="Amount"
    />
  </div>

  <p class="my-3 text-danger" v-show="showValidation">
    <i class="bi bi-x-circle-fill"></i>
    Amount not valid.
  </p>

  <div class="row" style="margin-bottom: 22px;">
    <month-and-year
      ref="payableFrom"
      monthLabel="payable from month"
      yearLabel="year"
      ariaMonthLabel="Payable from month"
      ariaYearLabel="Payable from year"
    />
  </div>

  <div v-show="!isPayableForLife" class="row">
    <month-and-year
      ref="payableTo"
      monthLabel="payable to month"
      yearLabel="year"
      :defaultYearRange="40"
      ariaMonthLabel="Payable from month"
      ariaYearLabel="Payable from year"
    />
  </div>

  <div class="mb-3">
    <yes-no
      ref="increasesWithInflation"
      questionKey="willAmountIncrease"
      question="Does this increase with inflation?"
      :answer="amountWillIncrease"
      @answerChanged="onAmountWillIncreaseChanged"
    />
  </div>

  <bad-response-viewer ref="badResponseViewer" />

  <div class="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
    <lock-button v-show="showUpdateButton" :text="buttonText" :async-action="onSaveClicked"></lock-button>

    <button
      type="button"
      class="btn btn-secondary me-md-2"
      aria-label="Close"
      v-on:click="onCancelClicked"
    >
      {{ cancelButtonText }}
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, provide } from "vue";
import { inputValidator } from "@/db/ts/inputValidator";
import { OtherIncome } from "@/guiide/ts/otherIncome";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import Constants from "@/guiide/ts/constants";
import CurrencyInput from "../../../shared/components/CurrencyInput.vue";
import MonthAndYear from "../common/MonthAndYear.vue";
import YesNo from "../common/YesNo.vue";
import LockButton from "@/guiide/components/common/LockButton.vue";
import BadResponseViewer from "../../../shared/components/BadResponseViewer.vue";
import { formatter } from "../../../db/ts/formatter";
import ResponseTracker from "@/shared/ts/responseTracker";
import { app } from "../../ts/app";
import Convert from "@/shared/ts/convert";
import { AxiosResponse } from "axios";
import { getOtherIncomeName } from "@/guiide/ts/utilities";
import EditMode from "@/guiide/ts/editMode";
import dateTimeProvider from "@/guiide/ts/systemDateTimeProvider";

export default defineComponent({
  name: "OtherIncomeEditor",
  emits: ["edit-finished"],
  components: {
    CurrencyInput,
    BadResponseViewer,
    MonthAndYear,
    YesNo,
    LockButton
  },
  setup() {
    const uiAmount = ref(null);
    const flagInvalidAnswer = ref(false);
    const payableFrom = ref(null);
    const payableTo = ref(null);
    const increasesWithInflation = ref(null);
    const badResponseViewer = ref(null);
    const execute = ref(false);

    provide('execute', execute);

    return {
      uiAmount,
      flagInvalidAnswer,
      payableFrom,
      payableTo,
      increasesWithInflation,
      badResponseViewer,
      execute
    };
  },
  data() {
    return {
      inputValidator,
      formatter,
      otherIncome: null as OtherIncome,
      name: "",
      amountWillIncrease: true,
      responseTracker: ResponseTracker.empty<AxiosResponse<unknown>>(),
      isPayableForLife: false,
      editMode: EditMode.Write
    };
  },
  computed: {
    showValidation: function() {
      return this.flagInvalidAnswer;
    },
    showUpdateButton: function(): boolean {
      return this.editMode === EditMode.Write;
    },
    cancelButtonText: function(): string {
      return this.editMode === EditMode.Write
        ? "Cancel"
        : "Close";
    },
    buttonText: function(): string {
      return (this.otherIncome?.id ?? Constants.NewId) === Constants.NewId
        ? "Add"
        : "Update";
    }
  },
  methods: {
    setValues(otherIncome: OtherIncome, trdYear: number) {
      this.badResponseViewer.clear();
      this.otherIncome = otherIncome;
      this.editMode = otherIncome.editMode;
      const readOnly = otherIncome.editMode === EditMode.ReadOnly;
      this.name = getOtherIncomeName(otherIncome);
      this.uiAmount.setValue(
        otherIncome.amount == 0 ? null : otherIncome.amount,
        readOnly
      );

      this.payableFrom.setFirstYear(trdYear);
      this.payableFrom.setSelectedValue(
        otherIncome.payableFromMonth,
        otherIncome.payableFromYear
      );
      this.payableFrom.setDisabledState(readOnly);

      this.payableTo.setFirstYear(trdYear);
      this.payableTo.setSelectedValue(
        otherIncome.payableToMonth,
        otherIncome.payableToYear
      );
      this.payableTo.setDisabledState(readOnly);

      this.amountWillIncrease = otherIncome.amountWillIncrease;
      this.increasesWithInflation.setDisabledState(readOnly);

      this.isPayableForLife = otherIncome.payableForLife;
    },
    onCancelClicked: function() {
      this.editIsFinished();
    },
    onSaveShortcut: async function() {
      this.execute = true;
    },
    onSaveClicked: async function() {
      if (!this.uiAmount.validate()) {
        this.execute = false;
        return;
      }

      const update = ObjectBuilder.createOtherIncome(
        this.otherIncome.id,
        this.otherIncome.name,
        this.uiAmount.getValue(),
        this.payableFrom.getSelectedMonth(),
        this.payableFrom.getSelectedYear(),
        this.payableTo.getSelectedMonth(),
        this.payableTo.getSelectedYear(),
        this.amountWillIncrease
      );

      const responseTracker = await app.updateOtherIncome(update);
      this.execute = false;

      if (responseTracker.isOk) {
        this.editIsFinished();
      } else {
        if (responseTracker.trackerWillHandleFailure) {
          responseTracker.handleFailure();
        } else {
          this.badResponseViewer.update(
            responseTracker.isOk,
            responseTracker.message
          );
        }
      }
    },
    editIsFinished() {
      this.uiAmount.hideAnyWarnings();
      this.$emit("edit-finished");
    },
    getYearsPayableFrom() {
      const currentYear = dateTimeProvider.now().getFullYear();
      const allYears = [];
      const lastYear = currentYear + 10;

      for (let i = currentYear; i <= lastYear; i++) {
        allYears.push(i);
      }

      return allYears;
    },
    onAmountWillIncreaseChanged(answer: string) {
      this.amountWillIncrease = Convert.toBoolean(answer);
    }
  }
});
</script>

<style>

</style>
